/**
 * ExpenseBasicInfoService
 *
 * Service to handle expense basic information business logic.
 * Manages categories, subcategories, icons, and form validation.
 */
import type { LucideIcon } from 'lucide-react';

import type { ComponentType, SVGProps } from 'react';

import { normalizeDecimalInput } from '@splitfiesta/core';
import { MAX_AMOUNT } from '@splitfiesta/shared';

import { ACCOMMODATION_SUBCATEGORY_ICONS } from '@/utils/accommodationSubcategories';
import { DRINKS_SUBCATEGORY_ICONS } from '@/utils/drinksSubcategories';
import { ENTERTAINMENT_SUBCATEGORY_ICONS } from '@/utils/entertainmentSubcategories';
import { getExpenseCategoryIcon } from '@/utils/expenseCategoryIcons';
import { FOOD_SUBCATEGORY_ICONS } from '@/utils/foodSubcategories';
import { HEALTHCARE_SUBCATEGORY_ICONS } from '@/utils/healthcareSubcategories';
import { SHOPPING_SUBCATEGORY_ICONS } from '@/utils/shoppingSubcategories';
import { TRANSPORT_SUBCATEGORY_ICONS } from '@/utils/transportSubcategories';
import { UTILITIES_SUBCATEGORY_ICONS } from '@/utils/utilitiesSubcategories';

/**
 * Category data structure
 */
export interface CategoryData {
  value: string;
  label: string;
  icon: LucideIcon | ComponentType<SVGProps<SVGSVGElement>>;
  hasSubcategories?: boolean;
}

/**
 * Subcategory icon entry
 */
export type SubcategoryIconEntry = [
  string,
  LucideIcon | ComponentType<SVGProps<SVGSVGElement>>,
];

/**
 * Category configuration
 */
export interface CategoryConfig {
  categories: CategoryData[];
  currentCategory: CategoryData;
}

/**
 * Service class for expense basic info operations
 */
export class ExpenseBasicInfoService {
  /**
   * Categories that have subcategory icons
   */
  private static readonly CATEGORIES_WITH_SUBCATEGORIES = [
    'food',
    'drinks',
    'transport',
    'accommodation',
    'entertainment',
    'shopping',
    'utilities',
    'healthcare',
  ];

  /**
   * Static map of subcategory icons (loaded immediately)
   */
  private static readonly subcategoryIconsMap = new Map<
    string,
    Record<string, LucideIcon | ComponentType<SVGProps<SVGSVGElement>>>
  >([
    [
      'food',
      FOOD_SUBCATEGORY_ICONS as Record<
        string,
        LucideIcon | ComponentType<SVGProps<SVGSVGElement>>
      >,
    ],
    [
      'drinks',
      DRINKS_SUBCATEGORY_ICONS as Record<
        string,
        LucideIcon | ComponentType<SVGProps<SVGSVGElement>>
      >,
    ],
    [
      'transport',
      TRANSPORT_SUBCATEGORY_ICONS as Record<
        string,
        LucideIcon | ComponentType<SVGProps<SVGSVGElement>>
      >,
    ],
    [
      'accommodation',
      ACCOMMODATION_SUBCATEGORY_ICONS as Record<
        string,
        LucideIcon | ComponentType<SVGProps<SVGSVGElement>>
      >,
    ],
    [
      'entertainment',
      ENTERTAINMENT_SUBCATEGORY_ICONS as Record<
        string,
        LucideIcon | ComponentType<SVGProps<SVGSVGElement>>
      >,
    ],
    [
      'shopping',
      SHOPPING_SUBCATEGORY_ICONS as Record<
        string,
        LucideIcon | ComponentType<SVGProps<SVGSVGElement>>
      >,
    ],
    [
      'utilities',
      UTILITIES_SUBCATEGORY_ICONS as Record<
        string,
        LucideIcon | ComponentType<SVGProps<SVGSVGElement>>
      >,
    ],
    [
      'healthcare',
      HEALTHCARE_SUBCATEGORY_ICONS as Record<
        string,
        LucideIcon | ComponentType<SVGProps<SVGSVGElement>>
      >,
    ],
  ]);

  /**
   * Get subcategory icons for a specific category
   */
  static getSubcategoryIcons(categoryValue: string): SubcategoryIconEntry[] {
    const icons = this.subcategoryIconsMap.get(categoryValue);
    if (!icons) return [];

    return Object.entries(icons).filter(([key]) => key !== 'default');
  }

  /**
   * Get subcategory icons asynchronously (for backwards compatibility)
   * @deprecated Use getSubcategoryIcons instead - now synchronous
   */
  static async getSubcategoryIconsAsync(
    categoryValue: string
  ): Promise<SubcategoryIconEntry[]> {
    return this.getSubcategoryIcons(categoryValue);
  }

  /**
   * @deprecated No longer needed - kept for backwards compatibility
   */
  static getSubcategoryIconsSync(
    _categoryValue: string
  ): SubcategoryIconEntry[] {
    return [];
  }

  /**
   * Filter subcategory icons based on search term
   */
  static filterSubcategoryIcons(
    icons: SubcategoryIconEntry[],
    searchTerm: string
  ): SubcategoryIconEntry[] {
    if (!searchTerm) return icons.sort(([a], [b]) => a.localeCompare(b));

    return icons
      .filter(([key]) => key.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort(([a], [b]) => a.localeCompare(b));
  }

  /**
   * Check if a category has subcategories
   */
  static hasSubcategories(categoryValue: string): boolean {
    return this.CATEGORIES_WITH_SUBCATEGORIES.includes(categoryValue);
  }

  /**
   * Format subcategory name for display using translations
   */
  static formatSubcategoryName(
    subcategory: string,
    category?: string,
    t?: (key: string) => string
  ): string {
    if (!subcategory || subcategory === 'default') return '';

    // If translation function and category are provided, try to get translated name
    if (t && category) {
      const translationKey = `expenses:subcategories.${category}.${subcategory}`;
      const translated = t(translationKey);

      // If translation exists (doesn't return the key itself), use it
      if (translated !== translationKey) {
        return translated;
      }
    }

    // Fallback to formatted original subcategory name
    return subcategory.charAt(0).toUpperCase() + subcategory.slice(1);
  }

  /**
   * Build category configuration
   */
  static buildCategoryConfig(
    category: string,
    subcategory: string | undefined,
    t: (key: string) => string
  ): CategoryConfig {
    // Create base categories
    const categoriesData: CategoryData[] = [
      {
        value: 'accommodation',
        label: t('expenses:categories.accommodation'),
        icon: getExpenseCategoryIcon(
          'accommodation',
          category === 'accommodation' ? subcategory : undefined
        ),
        hasSubcategories: true,
      },
      {
        value: 'education',
        label: t('expenses:categories.education'),
        icon: getExpenseCategoryIcon('education'),
        hasSubcategories: false,
      },
      {
        value: 'entertainment',
        label: t('expenses:categories.entertainment'),
        icon: getExpenseCategoryIcon(
          'entertainment',
          category === 'entertainment' ? subcategory : undefined
        ),
        hasSubcategories: true,
      },
      {
        value: 'food',
        label: t('expenses:categories.food'),
        icon: getExpenseCategoryIcon(
          'food',
          category === 'food' ? subcategory : undefined
        ),
        hasSubcategories: true,
      },
      {
        value: 'drinks',
        label: t('expenses:categories.drinks'),
        icon: getExpenseCategoryIcon(
          'drinks',
          category === 'drinks' ? subcategory : undefined
        ),
        hasSubcategories: true,
      },
      {
        value: 'healthcare',
        label: t('expenses:categories.healthcare'),
        icon: getExpenseCategoryIcon(
          'healthcare',
          category === 'healthcare' ? subcategory : undefined
        ),
        hasSubcategories: true,
      },
      {
        value: 'shopping',
        label: t('expenses:categories.shopping'),
        icon: getExpenseCategoryIcon(
          'shopping',
          category === 'shopping' ? subcategory : undefined
        ),
        hasSubcategories: true,
      },
      {
        value: 'transport',
        label: t('expenses:categories.transport'),
        icon: getExpenseCategoryIcon(
          'transport',
          category === 'transport' ? subcategory : undefined
        ),
        hasSubcategories: true,
      },
      {
        value: 'utilities',
        label: t('expenses:categories.utilities'),
        icon: getExpenseCategoryIcon(
          'utilities',
          category === 'utilities' ? subcategory : undefined
        ),
        hasSubcategories: true,
      },
    ];

    // Sort alphabetically by translated label
    const sortedCategories = categoriesData.sort((a, b) =>
      a.label.localeCompare(b.label)
    );

    // Add "Other" at the beginning
    const categories: CategoryData[] = [
      {
        value: 'other',
        label: t('expenses:categories.other'),
        icon: getExpenseCategoryIcon('other'),
        hasSubcategories: false,
      },
      ...sortedCategories,
    ];

    // Find current category or default to last one
    const currentCategory =
      categories.find(cat => cat.value === category) ||
      categories[categories.length - 1];

    return {
      categories,
      currentCategory,
    };
  }

  /**
   * Validate expense amount
   */
  static validateAmount(amount: string | undefined): {
    isValid: boolean;
    numericAmount: number;
    error?: string;
  } {
    if (!amount || amount === '') {
      return { isValid: false, numericAmount: 0, error: 'Amount is required' };
    }

    const numericAmount = normalizeDecimalInput(amount);

    if (isNaN(numericAmount)) {
      return { isValid: false, numericAmount: 0, error: 'Invalid amount' };
    }

    if (numericAmount <= 0) {
      return {
        isValid: false,
        numericAmount: 0,
        error: 'Amount must be greater than 0',
      };
    }

    if (numericAmount > MAX_AMOUNT) {
      return { isValid: false, numericAmount: 0, error: 'Amount is too large' };
    }

    return { isValid: true, numericAmount };
  }

  /**
   * Validate expense title
   */
  static validateTitle(title: string): {
    isValid: boolean;
    sanitizedTitle: string;
    error?: string;
  } {
    const trimmedTitle = title.trim();

    if (!trimmedTitle) {
      return { isValid: false, sanitizedTitle: '', error: 'Title is required' };
    }

    if (trimmedTitle.length > 200) {
      return {
        isValid: false,
        sanitizedTitle: trimmedTitle,
        error: 'Title is too long',
      };
    }

    return { isValid: true, sanitizedTitle: trimmedTitle };
  }

  /**
   * Get category button classes based on selection state
   */
  static getCategoryButtonClasses(
    isSelected: boolean,
    isSubcategory = false
  ): string {
    const baseClasses = 'flex flex-col items-center p-3 border-2 rounded-lg';

    if (isSubcategory) {
      return `${baseClasses} h-auto ${
        isSelected
          ? 'border-orange-500 dark:border-orange-400 bg-orange-50 dark:bg-orange-900/20'
          : 'border-gray-200 dark:border-gray-700 bg-[#FCFCFA] dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
      }`;
    }

    return `${baseClasses} min-h-[80px] h-auto ${
      isSelected
        ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
        : 'border-gray-200 dark:border-gray-700 bg-[#FCFCFA] dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
    }`;
  }

  /**
   * Get icon classes based on selection state
   */
  static getIconClasses(isSelected: boolean, isSubcategory = false): string {
    if (isSubcategory) {
      return `w-6 h-6 ${
        isSelected
          ? 'text-orange-600 dark:text-orange-400'
          : 'text-gray-700 dark:text-gray-300'
      }`;
    }

    return 'w-6 h-6 mb-2';
  }

  /**
   * Get text classes for subcategory labels
   */
  static getSubcategoryTextClasses(isSelected: boolean): string {
    return `text-xs text-center mt-1 ${
      isSelected
        ? 'text-orange-700 dark:text-orange-300'
        : 'text-gray-600 dark:text-gray-400'
    }`;
  }

  /**
   * Parse numeric amount from string
   */
  static parseNumericAmount(value: string | undefined): number {
    if (!value) return 0;
    const parsed = normalizeDecimalInput(value);
    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Check if should show subcategory selector
   */
  static shouldShowSubcategorySelector(
    categoryValue: string,
    showSubcategoryIcons: boolean,
    selectedCategoryForIcons: string
  ): boolean {
    return (
      showSubcategoryIcons &&
      selectedCategoryForIcons === categoryValue &&
      this.hasSubcategories(categoryValue)
    );
  }

  /**
   * Handle category selection logic
   */
  static handleCategorySelection(
    categoryValue: string,
    callbacks: {
      setCategory: (value: string) => void;
      setSubcategory?: (value: string | undefined) => void;
      handleCategoryChange?: (value: string) => void;
      setSelectedCategoryForIcons: (value: string) => void;
      setShowSubcategoryIcons: (value: boolean) => void;
      setShowAllCategories: (value: boolean) => void;
      setSubcategorySearch: (value: string) => void;
    }
  ): void {
    const hasSubcategories = this.hasSubcategories(categoryValue);

    if (hasSubcategories) {
      // Show subcategory selection
      callbacks.setSelectedCategoryForIcons(categoryValue);
      callbacks.setSubcategorySearch('');
      callbacks.setShowSubcategoryIcons(true);
      callbacks.setShowAllCategories(false);
    } else {
      // Direct category selection
      if (callbacks.handleCategoryChange) {
        callbacks.handleCategoryChange(categoryValue);
      } else {
        callbacks.setCategory(categoryValue);
      }
      callbacks.setShowAllCategories(false);
    }
  }

  /**
   * Handle subcategory selection logic
   */
  static handleSubcategorySelection(
    categoryValue: string,
    subcategoryValue: string,
    callbacks: {
      setCategory: (value: string) => void;
      setSubcategory?: (value: string | undefined) => void;
      setShowSubcategoryIcons: (value: boolean) => void;
      setSelectedCategoryForIcons: (value: string) => void;
      setSubcategorySearch: (value: string) => void;
      setShowAllCategories: (value: boolean) => void;
    }
  ): void {
    callbacks.setCategory(categoryValue);

    if (callbacks.setSubcategory) {
      callbacks.setSubcategory(subcategoryValue);
    }

    // Reset UI state
    callbacks.setShowSubcategoryIcons(false);
    callbacks.setSelectedCategoryForIcons('');
    callbacks.setSubcategorySearch('');
    callbacks.setShowAllCategories(false);
  }

  /**
   * Reset category UI state
   */
  static resetCategoryUI(callbacks: {
    setShowAllCategories: (value: boolean) => void;
    setShowSubcategoryIcons: (value: boolean) => void;
    setSelectedCategoryForIcons: (value: string) => void;
    setSubcategorySearch: (value: string) => void;
  }): void {
    callbacks.setShowAllCategories(false);
    callbacks.setShowSubcategoryIcons(false);
    callbacks.setSelectedCategoryForIcons('');
    callbacks.setSubcategorySearch('');
  }
}

export const expenseBasicInfoService = new ExpenseBasicInfoService();
